// Messaging with the embedded OptimusZapp screens (/embed/*), per the OptimusZapp embeds guide:
// - every iframe gets its own single-use launch ticket (a ticket can be exchanged once);
// - each iframe's origin is the origin of the launch URL the backend got from OptimusZapp for it ("embed_origin":
//   the app's domain, its default host or a gym's white-label domain) — never a configured, guessed or pattern host;
// - messages go only to that frame's origin (never "*");
// - messages are accepted only from one of our iframes, on that frame's origin, in a documented shape.

export const PROVIDE_LAUNCH_TICKET = "OPTIMUS_PROVIDE_LAUNCH_TICKET";
export const REQUEST_LAUNCH_TICKET = "OPTIMUS_REQUEST_LAUNCH_TICKET";
const READY_TYPES = new Set(["OPTIMUS_EMBED_READY", "OPTIMUSZAPP_READY"]);
const INBOUND_TYPES = new Set([
  ...READY_TYPES,
  REQUEST_LAUNCH_TICKET,
  "OPTIMUS_CONVERSATION_SELECTED",
  "OPTIMUS_PHONE_LINE_CHANGED",
  "OPTIMUS_BILLING_TAB_CHANGED",
  "OPTIMUSZAPP_DRAWER_STATE_CHANGE",
]);

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1"]);

function isLocalDevHost(hostname) {
  return LOOPBACK_HOSTS.has(hostname) || hostname.endsWith(".localhost");
}

function parsedSecureUrl(url) {
  try {
    const parsed = new URL(url);
    const secure = parsed.protocol === "https:" || (parsed.protocol === "http:" && isLocalDevHost(parsed.hostname));
    return secure && !parsed.username && !parsed.password ? parsed : null;
  } catch {
    return null; // not an absolute URL
  }
}

// The exact origin of an embed URL, or null when it is not https (http only for local development hosts).
export function embedOriginOf(url) {
  return parsedSecureUrl(url)?.origin || null;
}

// The launch URL with theme parameters, or "" unless it is an /embed/ screen on expectedOrigin — the embed_origin the
// backend returned with this launch URL.
export function themedEmbedUrl(launchUrl, expectedOrigin, params = {}) {
  if (!launchUrl || !expectedOrigin || embedOriginOf(launchUrl) !== expectedOrigin) return "";
  const url = new URL(launchUrl);
  if (!url.pathname.startsWith("/embed/")) return "";
  Object.entries({ embedded: "true", ...params }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") url.searchParams.set(key, String(value));
  });
  return url.toString();
}

// Posts to one embedded screen with its exact origin as targetOrigin; does nothing without both.
export function postToEmbed(frameWindow, origin, message) {
  if (!frameWindow || !origin || origin === "*") return false;
  frameWindow.postMessage(message, origin);
  return true;
}

// A message comes from an embedded screen only when it is from that frame's window, on that frame's origin, and has
// one of the documented shapes.
export function isTrustedEmbedMessage(event, expectedOrigin, frameWindow) {
  if (!event || !expectedOrigin || event.origin !== expectedOrigin) return false;
  if (!frameWindow || event.source !== frameWindow) return false;
  const data = event.data;
  return Boolean(data) && typeof data === "object" && !Array.isArray(data) && typeof data.type === "string" && INBOUND_TYPES.has(data.type);
}

// Handles one window "message" event for a page with embedded screens.
// frames: [{ surface, memberId?, window, origin }] — the page's iframes, the surface each shows and the embed_origin
// its launch URL came with. requestTicket(frame) resolves to the backend's launch-ticket answer for THAT frame
// ({ ticket, embed_origin }, or null); the ticket is posted only when it was issued for the frame's origin.
// onReady(frame) re-sends the theme. Returns "ready", "ticket" or null (ignored).
export async function handleEmbedMessage(event, { frames, requestTicket, onReady }) {
  const frame = (frames || []).find((f) => f && f.window && f.window === event?.source);
  if (!frame || !isTrustedEmbedMessage(event, frame.origin, frame.window)) return null;

  if (READY_TYPES.has(event.data.type)) {
    if (onReady) onReady(frame);
    return "ready";
  }
  if (event.data.type === REQUEST_LAUNCH_TICKET) {
    const answer = await requestTicket(frame);
    const ticket = answer && answer.embed_origin === frame.origin ? answer.ticket : null;
    if (ticket) postToEmbed(frame.window, frame.origin, { type: PROVIDE_LAUNCH_TICKET, ticket });
    return ticket ? "ticket" : null;
  }
  return null;
}

// Refresh tickets: an embedded screen asks again whenever a ticket does not arrive, so a refusal must not turn into a
// request loop. Per iframe (key), only network failures and 5xx answers are retried — at most 3 attempts with
// backoff; any other refusal (4xx) stops at once. After giving up the key is stopped: its requests are not answered
// until retry(key), which the page calls from its "Retry" button.
export const TICKET_RETRY_DELAYS_MS = [1000, 3000];

export function isRetryableTicketError(err) {
  const status = err?.response?.status;
  return !status || status >= 500;
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function createTicketRefresher({ onGiveUp, delaysMs = TICKET_RETRY_DELAYS_MS, sleep = wait } = {}) {
  const stopped = new Set();
  const pending = new Set();

  async function attempt(fetchTicket) {
    for (let i = 0; ; i += 1) {
      try {
        return await fetchTicket();
      } catch (err) {
        if (!isRetryableTicketError(err) || i >= delaysMs.length) throw err;
        await sleep(delaysMs[i]);
      }
    }
  }

  return {
    // The ticket answer for key, or null when key is stopped, already being asked for, or gives up now.
    async request(key, fetchTicket) {
      if (stopped.has(key) || pending.has(key)) return null;
      pending.add(key);
      try {
        return (await attempt(fetchTicket)) || null;
      } catch (err) {
        stopped.add(key);
        if (onGiveUp) onGiveUp(key, err);
        return null;
      } finally {
        pending.delete(key);
      }
    },
    isStopped: (key) => stopped.has(key),
    retry: (key) => stopped.delete(key),
  };
}

// The single-use URL that opens the full OptimusZapp dashboard (/sso?code=…), or "" unless it is https (http only for
// local development hosts) on the /sso entry.
export function dashboardUrlOf(launchUrl) {
  const parsed = parsedSecureUrl(launchUrl);
  return parsed && parsed.pathname === "/sso" ? parsed.toString() : "";
}

// Opens the dashboard in a new tab without giving it a handle to this page. Returns false when the URL is refused.
export function openDashboard(launchUrl, openWindow = window.open) {
  const url = dashboardUrlOf(launchUrl);
  if (!url) return false;
  openWindow(url, "_blank", "noopener,noreferrer");
  return true;
}
